<script lang="ts">
import SuperAdminLayout from "@/layouts/SuperAdminLayout.vue"

export default { layout: SuperAdminLayout }
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { computed, ref } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import FlashAlerts from "@/components/FlashAlerts.vue"
import LabeledSection from "@/components/LabeledSection.vue"
import { ICON } from "@/lib/icons"
import { downloadSuperAdminExportPath, superAdminExportPath, superAdminExportsPath } from "@/routes"
import type { ExportEntry } from "@/types/models"

const props = defineProps<{
  exports: ExportEntry[]
}>()

const exporting = ref(false)
const deleteTarget = ref<string | null>(null)
const deleteOpen = computed(() => deleteTarget.value !== null)
const deleting = ref(false)

function exportAll() {
  exporting.value = true
  router.post(superAdminExportsPath(), {}, { onFinish: () => (exporting.value = false) })
}

function confirmDelete() {
  if (!deleteTarget.value) return
  deleting.value = true
  router.delete(superAdminExportPath(deleteTarget.value), {
    onFinish: () => {
      deleting.value = false
      deleteTarget.value = null
    },
  })
}
</script>

<template>
  <Head title="Backups — Admin" />

  <div class="flex flex-col gap-6">
    <FlashAlerts />

    <SbList bordered separator>
      <SbListItem>
        <SbListSection avatar>
          <UIcon :name="ICON.export" class="text-muted size-5" />
        </SbListSection>
        <SbListSection>
          <SbListLabel>Export all accounts</SbListLabel>
          <SbListLabel caption>System-wide backup of all inventory data</SbListLabel>
        </SbListSection>
        <SbListSection side>
          <UButton
            label="Export"
            color="neutral"
            variant="subtle"
            size="sm"
            :loading="exporting"
            @click="exportAll"
          />
        </SbListSection>
      </SbListItem>
    </SbList>

    <LabeledSection
      v-if="props.exports.length"
      title="Previous backups"
      :count="props.exports.length"
    >
      <SbListItem v-for="exp in props.exports" :key="exp.name">
        <SbListSection avatar>
          <UIcon :name="ICON.archive" class="text-muted size-5" />
        </SbListSection>
        <SbListSection>
          <SbListLabel :lines="1">{{ exp.name }}</SbListLabel>
          <SbListLabel caption>{{ exp.size_human }} · {{ exp.modified_at_human }}</SbListLabel>
        </SbListSection>
        <SbListSection side>
          <div class="flex items-center gap-1">
            <UButton
              :icon="ICON.export"
              color="neutral"
              variant="ghost"
              size="xs"
              square
              :to="downloadSuperAdminExportPath(exp.name)"
              external
            />
            <UButton
              :icon="ICON.delete"
              color="neutral"
              variant="ghost"
              size="xs"
              square
              @click="
                () => {
                  deleteTarget = exp.name
                }
              "
            />
          </div>
        </SbListSection>
      </SbListItem>
    </LabeledSection>

    <DeleteConfirmModal
      :open="deleteOpen"
      :title="`Delete ${deleteTarget}?`"
      :loading="deleting"
      @update:open="!$event && (deleteTarget = null)"
      @confirm="confirmDelete"
    >
      <p class="text-sm">This backup file will be permanently deleted.</p>
    </DeleteConfirmModal>
  </div>
</template>
